import cx from 'classnames';
import moment from 'moment';
import { InvoiceDetailReportModel } from '../../api/models/invoiceDetailReport';
import {
    bookkeeperEmail, bookkeeperName, bookkeeperPhoneNumber, providerAddress, providerCityStateZip,
    providerFaxNumber, providerFederalEin, providerName, providerPhoneNumber
} from '../../common/constants';


// Variables & general utility functions
//~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
const dateRangeFormat: string = 'MM/DD/YYYY';

type InvoiceDetailProps = {
    report: InvoiceDetailReportModel | undefined,
    servicePeriod: string;
    navigateToTimesheetWeek: Function;
}
//~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~


// Main component
//~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
const InvoiceSummaryReport = ({ report, servicePeriod, navigateToTimesheetWeek }: InvoiceDetailProps) => {
    return (
        <div id='detail-report'>
            <div id="report-header" className="flex justify-between">
                <div id="report-header-billing-section" className="w-1/3 text-left">
                    <p>Bill To:</p>
                    <h3 className="font-bold">{report?.districtName}</h3>
                    <h3 className="font-bold">{report?.districtAddress1}</h3>
                    <h3 className="font-bold">{report?.districtAddress2}</h3>
                    <h3 className="font-bold">{report?.districtCityStateZip}</h3>
                </div>
                <div id="report-header-invoice-section" className="w-1/3 text-center">
                    <span className={
                        cx({
                            'text-xl font-bold': true,
                            'text-red-500': !report?.exportedOn,
                            'text-ptsgreen-normal': report?.exportedOn
                        })
                    }>
                        {report?.invoiceNumber
                            ? <h1>INVOICE #{report?.invoiceNumber}</h1>
                            : <h1>DRAFT INVOICE PREVIEW</h1>
                        }
                    </span>
                    <h3>Service Period: {servicePeriod}</h3>
                </div>
                <div id="report-header-provider-section" className="w-1/3 text-right">
                    <h2 className="font-bold">{providerName}</h2>
                    <h2 className="font-bold">{providerFederalEin}</h2>
                    <h4>{providerAddress}</h4>
                    <h4>{providerCityStateZip}</h4>
                    <h4>Phone: {providerPhoneNumber} &nbsp; Fax: {providerFaxNumber}</h4>
                </div>
            </div>
            <hr className="border-black border-2 my-2"></hr>
            {(!report || report.topLevelGroups?.length === 0) && (
                <div className="no-report-data">
                    <h2 className="text-center">No report data. Please check the District ID and service period.</h2>
                </div>
            )}
            {(report?.topLevelGroups && report.topLevelGroups.length > 0) && (
                <div className="report-body my-2">
                    <h3 className="font-bold text-center my-2">Time Detail (in minutes)</h3>
                    <div className='summary-header sticky top-0 bg-white font-bold'>
                        <div className="flex items-end">
                            <div className='w-1/6'>
                            </div>
                            <div className='w-5/6 text-center flex justify-between'>
                                <h4 className='text-sm mx-2 w-20'>
                                    Treatment Time Covered
                                </h4>
                                <div className="w-60">
                                    <hr className="border-black border-1"></hr>
                                    <h3 className="text-center">Student Time</h3>
                                    <hr className="border-black border-1"></hr>
                                    <div className="flex justify-between items-center h-10">
                                        <h4 className='text-sm mr-2 w-14'>
                                            Direct
                                        </h4>
                                        <h4 className='text-sm mx-2 w-14'>
                                            Consult
                                        </h4>
                                        <h4 className='text-sm mx-2 w-14'>
                                            ReEval
                                        </h4>
                                        <h4 className='text-sm ml-2 w-14'>
                                            Indirect
                                        </h4>
                                    </div>
                                </div>
                                <div className="w-48">
                                    <hr className="border-black border-1"></hr>
                                    <h3 className="text-center">New Diag.</h3>
                                    <hr className="border-black border-1"></hr>
                                    <div className="flex justify-between items-center h-10">
                                        <h4 className='text-sm mr-2 w-14'>
                                            Screen
                                        </h4>
                                        <h4 className='text-sm mx-2 w-20'>
                                            Intervention
                                        </h4>
                                        <h4 className='text-sm ml-2 w-10'>
                                            Eval
                                        </h4>
                                    </div>
                                </div>
                                <div className="w-8">
                                    <hr className="border-black border-1"></hr>
                                    <h3 className="text-center">EI</h3>
                                    <hr className="border-black border-1"></hr>
                                    <div className="flex justify-between items-center h-10">
                                        <h4 className='text-sm mx-2 w-8'>
                                            EI
                                        </h4>
                                    </div>
                                </div>
                                <div className="w-48">
                                    <hr className="border-black border-1"></hr>
                                    <h3 className="text-center">Non-Student</h3>
                                    <hr className="border-black border-1"></hr>
                                    <div className="flex justify-between items-center h-10">
                                        <h4 className='text-sm mr-2 w-10'>
                                            Other
                                        </h4>
                                        <h4 className='text-sm mx-2 w-8'>
                                            MTSS
                                        </h4>
                                        <h4 className='text-sm ml-2 w-20'>
                                            Clinical Supervision
                                        </h4>
                                    </div>
                                </div>
                                <h4 className='text-sm text-right ml-2 w-12 flex items-end'>
                                    HOURS
                                </h4>
                            </div>
                        </div>
                        <hr className="border-black border-1"></hr>
                    </div>

                    {report?.topLevelGroups?.map((topLevelGroup, gIdx) => (
                        <div key={`topLevelSection_${gIdx}`} className="top-level-section">
                            {topLevelGroup.disciplines?.map((discipline, dIdx) => (
                                <div key={`disciplineSection_${dIdx}`} className="discipline-section">
                                    <h2 className="top-level-group-name font-bold">{topLevelGroup.topLevelGroupName}</h2>
                                    <h3 className="discipline-name font-bold">{discipline.disciplineName}</h3>
                                    {discipline.therapists?.map((therapist, tIdx) => (
                                        <div key={`therapistSection_${tIdx}`} className="therapist-section">
                                            <h4 className="font-bold mt-1">{therapist.therapistLastName}, {therapist.therapistFirstName}</h4>
                                            <hr className="border-gray border-1"></hr>
                                            {therapist.entryDays?.map((day, dIdx) => (
                                                <div key={`dayRow_${dIdx}`}
                                                    className="day-row-wrapper hover:bg-ptsblue-normal hover:text-white cursor-pointer text-sm"
                                                    onClick={(e) => navigateToTimesheetWeek(day.weekId)}>
                                                    <div className="day-row flex">
                                                        <div className='w-1/6 flex items-center'>
                                                            <span className="data-date ml-5">{moment(day.dateOfEntry).format(dateRangeFormat)}</span>
                                                        </div>
                                                        <div className='w-5/6 text-center flex justify-between items-center'>
                                                            <span className="data-treatment-time-covered mx-2 w-20">{day.treatmentTimeCovered}</span>
                                                            <div className="w-60"
                                                                style={{ borderLeft: '1px', borderRight: '1px', borderStyle: 'solid', borderColor: 'gray' }}>
                                                                <div className="flex justify-between items-center h-8">
                                                                    <span className="data-direct mr-2 w-14">{day.directMinutes}</span>
                                                                    <span className="data-consult mx-2 w-14">{day.consultMinutes}</span>
                                                                    <span className="data-reeval mx-2 w-14">{day.reEvalMinutes}</span>
                                                                    <span className="data-indirect ml-2 w-14">{day.indirectMinutes}</span>
                                                                </div>
                                                            </div>
                                                            <div className="w-48"
                                                                style={{ borderLeft: '1px', borderRight: '1px', borderStyle: 'solid', borderColor: 'gray' }}>
                                                                <div className="flex justify-between items-center h-8">
                                                                    <span className="data-screen mr-2 w-14">{day.screenMinutes}</span>
                                                                    <span className="data-intervention mx-2 w-20">{day.interventionMinutes}</span>
                                                                    <span className="data-eval ml-2 w-10">{day.evalMinutes}</span>
                                                                </div>
                                                            </div>
                                                            <div className="w-8"
                                                                style={{ borderLeft: '1px', borderRight: '1px', borderStyle: 'solid', borderColor: 'gray' }}>
                                                                <div className="flex justify-between items-center h-8">
                                                                    <span className="data-ei mx-2 w-8">{day.eiMinutes}</span>
                                                                </div>
                                                            </div>
                                                            <div className="w-48"
                                                                style={{ borderLeft: '1px', borderRight: '1px', borderStyle: 'solid', borderColor: 'gray' }}>
                                                                <div className="flex justify-between items-center h-8">
                                                                    <span className="data-other mr-2 w-10">{day.otherMinutes}</span>
                                                                    <span className="data-mtss mx-2 w-8">{day.mtssMinutes}</span>
                                                                    <span className="data-clinical-supervision ml-2 w-20">{day.clinicalSupervisionMinutes}</span>
                                                                </div>
                                                            </div>
                                                            <span className="data-total-hours ml-2 w-12">{day.dateTotalHours.toFixed(2)}</span>

                                                        </div>
                                                    </div>
                                                    <hr className="border-gray border-1"></hr>
                                                </div>
                                            ))}
                                            <h4 className="font-bold text-right mr-2">{therapist.therapistTotalHours.toFixed(2)}</h4>
                                        </div>
                                    ))}
                                    <hr className="border-black border-1"></hr>
                                    <h3 className="text-right font-bold mr-2">Total {discipline.disciplineName}: {discipline.disciplineTotalHours.toFixed(2)}</h3>
                                </div>
                            ))}
                        </div>
                    ))}
                </div>
            )}
            <hr className="border-black border-2 my-2"></hr>
            <div id="report-footer" className="mt-5">
                <p>Please contact your billing specialist.</p>
            </div>

        </div>
    )
}
//~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~

export default InvoiceSummaryReport;
